import { useEffect, useState } from "react";
import { api, downloadFile } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => (v === "" || v == null ? "" : Number(v).toLocaleString("id-ID", { maximumFractionDigits: 4 }));

function CardTable({ card }) {
  const heads = ["Hari / Tanggal", "Jumlah di Gudang", "Masuk", "Keluar", "Total Sisa", "Paraf", "No. Tracking"];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border" data-testid="stock-card-table">
        <thead className="bg-muted"><tr>{heads.map((h) => <th key={h} className="border px-2 py-2 text-xs font-bold">{h}</th>)}</tr></thead>
        <tbody>
          {card.rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground" data-testid="stock-card-empty">Belum ada mutasi pada periode ini.</td></tr>}
          {card.rows.map((r, i) => (
            <tr key={i} data-testid={`stock-card-row-${i}`} className="text-center">
              <td className="border px-2 py-1.5">{r.hari_tanggal}</td>
              <td className="border px-2">{fmt(r.opening)}</td>
              <td className="border px-2 text-emerald-700">{r.qty_in ? fmt(r.qty_in) : ""}</td>
              <td className="border px-2 text-red-700">{r.qty_out ? fmt(r.qty_out) : ""}</td>
              <td className="border px-2 font-semibold">{fmt(r.balance)}</td>
              <td className="border px-2">{r.paraf}</td>
              <td className="border px-2 font-mono text-xs">{r.tracking_no}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function StockCard() {
  const [materials, setMaterials] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [f, setF] = useState({ material_id: "", supplier_id: "all", date_from: "", date_to: "" });
  const [card, setCard] = useState(null);

  useEffect(() => {
    api.get("/materials").then((r) => setMaterials(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
  }, []);

  const qs = () => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") p.append(k, v); });
    return p.toString();
  };

  useEffect(() => {
    if (!f.material_id) return;
    api.get(`/stock-card?${qs()}`).then((r) => setCard(r.data)).catch(() => toast.error("Gagal memuat kartu stok."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f]);

  async function dl(kind) {
    const code = card?.material?.code || "kartu";
    try {
      await downloadFile(`/reports/mj-form-02.${kind}?${qs()}`, `MJ-FORM-02_${code}.${kind}`);
      toast.success("File berhasil diunduh.");
    } catch { toast.error("Gagal mengunduh file."); }
  }

  return (
    <div className="space-y-5">
      <div><h1 className="font-heading text-2xl font-extrabold">Kartu Stok Gudang (MJ-FORM-02)</h1>
        <p className="text-sm text-muted-foreground">Kartu stok per bahan baku, dihitung otomatis dari mutasi masuk, keluar (FIFO produksi), dan koreksi.</p></div>
      <Card className="p-5 space-y-4">
        <div className="grid sm:grid-cols-4 gap-4">
          <div><Label className="text-xs">Bahan Baku</Label>
            <Select value={f.material_id} onValueChange={(v) => setF({ ...f, material_id: v })}>
              <SelectTrigger data-testid="sc-material" className="mt-1"><SelectValue placeholder="Pilih bahan" /></SelectTrigger>
              <SelectContent>{materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.code} — {m.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Pemasok</Label>
            <Select value={f.supplier_id} onValueChange={(v) => setF({ ...f, supplier_id: v })}>
              <SelectTrigger data-testid="sc-supplier" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua pemasok</SelectItem>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Dari Tanggal</Label><Input type="date" data-testid="sc-from" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Sampai Tanggal</Label><Input type="date" data-testid="sc-to" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="mt-1" /></div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button data-testid="sc-dl-pdf" disabled={!card} onClick={() => dl("pdf")}><FileDown className="w-4 h-4 mr-1" /> Download MJ-FORM-02 (PDF)</Button>
          <Button variant="outline" data-testid="sc-dl-xlsx" disabled={!card} onClick={() => dl("xlsx")}><FileSpreadsheet className="w-4 h-4 mr-1" /> Excel XLSX</Button>
        </div>
      </Card>
      {card && (
        <Card className="p-5 space-y-3">
          <div className="text-sm space-y-0.5">
            <div data-testid="sc-title" className="font-heading font-bold">KARTU STOK {card.material.name}</div>
            <div data-testid="sc-supplier-label"><b>Nama/Kode Pemasok :</b> {card.supplier_label}</div>
            <div><b>Satuan :</b> {card.unit} · <b>Saldo akhir :</b> <span data-testid="sc-balance">{fmt(card.balance)}</span></div>
          </div>
          <CardTable card={card} />
        </Card>
      )}
    </div>
  );
}
